import * as THREE from 'three';
import type { CardDef } from '@plotjack/core';
import { cardArtUrl } from './assets';

const W = 512;
const H = 720;

const TYPE_COLORS: Record<string, [string, string]> = {
  twist: ['#7a3e9d', '#e7c2ff'],
  rule_breaker: ['#2f6f8f', '#bfe8ff'],
  chaos: ['#a8432a', '#ffd3b8'],
  counter: ['#8b6a1f', '#ffe9a8'],
  keepsake: ['#2f7a4f', '#c6f5d6'],
};

const TYPE_LABEL: Record<string, string> = {
  twist: 'Twist',
  rule_breaker: 'Rule Breaker',
  chaos: 'Chaos',
  counter: 'Counter',
  keepsake: 'Keepsake',
};

const cache = new Map<string, THREE.CanvasTexture>();
const imgCache = new Map<string, Promise<HTMLImageElement | null>>();

function loadImage(url: string): Promise<HTMLImageElement | null> {
  let p = imgCache.get(url);
  if (!p) {
    p = new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
    imgCache.set(url, p);
  }
  return p;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

function draw(ctx: CanvasRenderingContext2D, card: CardDef, art: HTMLImageElement | null, hue: number) {
  const [typeColor, typeInk] = TYPE_COLORS[card.type] ?? ['#6b5a3a', '#f5e6c8'];
  ctx.clearRect(0, 0, W, H);

  // body
  roundRect(ctx, 0, 0, W, H, 34);
  const body = ctx.createLinearGradient(0, 0, W, H);
  body.addColorStop(0, '#2a2033');
  body.addColorStop(1, '#120e17');
  ctx.fillStyle = body;
  ctx.fill();

  // art window
  const ax = 26;
  const ay = 26;
  const aw = W - 52;
  const ah = 430;
  ctx.save();
  roundRect(ctx, ax, ay, aw, ah, 20);
  ctx.clip();
  if (art) {
    const s = Math.max(aw / art.width, ah / art.height);
    const dw = art.width * s;
    const dh = art.height * s;
    ctx.drawImage(art, ax + (aw - dw) / 2, ay + (ah - dh) * 0.3, dw, dh);
  } else {
    const g = ctx.createLinearGradient(ax, ay, ax + aw, ay + ah);
    g.addColorStop(0, `hsl(${hue},45%,26%)`);
    g.addColorStop(1, `hsl(${hue},55%,12%)`);
    ctx.fillStyle = g;
    ctx.fillRect(ax, ay, aw, ah);
  }
  const fade = ctx.createLinearGradient(0, ay + ah - 90, 0, ay + ah);
  fade.addColorStop(0, 'rgba(18,14,23,0)');
  fade.addColorStop(1, 'rgba(18,14,23,0.85)');
  ctx.fillStyle = fade;
  ctx.fillRect(ax, ay + ah - 90, aw, 90);
  ctx.restore();

  // frame
  roundRect(ctx, 8, 8, W - 16, H - 16, 28);
  ctx.lineWidth = 6;
  ctx.strokeStyle = '#c9a227';
  ctx.stroke();
  roundRect(ctx, ax, ay, aw, ah, 20);
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(201,162,39,0.7)';
  ctx.stroke();

  // cost gem
  ctx.beginPath();
  ctx.arc(70, 70, 36, 0, Math.PI * 2);
  ctx.fillStyle = '#1a1428';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#c9a227';
  ctx.stroke();
  ctx.fillStyle = '#f5e6c8';
  ctx.font = '700 40px "Crimson Pro", Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(card.cost), 70, 72);

  // type ribbon
  const label = TYPE_LABEL[card.type] ?? card.type;
  ctx.font = '600 20px system-ui, sans-serif';
  const tw = ctx.measureText(label.toUpperCase()).width + 34;
  roundRect(ctx, W / 2 - tw / 2, ay + ah - 20, tw, 38, 19);
  ctx.fillStyle = typeColor;
  ctx.fill();
  ctx.fillStyle = typeInk;
  ctx.fillText(label.toUpperCase(), W / 2, ay + ah - 1);

  // name
  ctx.fillStyle = '#f5e6c8';
  ctx.font = '700 44px "Crimson Pro", Georgia, serif';
  const nameLines = wrap(ctx, card.name, W - 70).slice(0, 2);
  let y = ay + ah + 58;
  for (const l of nameLines) {
    ctx.fillText(l, W / 2, y);
    y += 44;
  }

  // rules text
  ctx.fillStyle = 'rgba(245,230,200,0.78)';
  ctx.font = 'italic 26px "Crimson Pro", Georgia, serif';
  const desc = wrap(ctx, card.description ?? '', W - 80).slice(0, nameLines.length > 1 ? 2 : 3);
  y += 4;
  for (const l of desc) {
    ctx.fillText(l, W / 2, y);
    y += 30;
  }
}

/** Composited card face (art + frame + name) as a texture. Redraws once the art loads. */
export function getCardTexture(card: CardDef, hue: number): THREE.CanvasTexture {
  const url = cardArtUrl(card.id);
  const cacheKey = `${card.id}:${url ?? 'ph'}`;
  const hit = cache.get(cacheKey);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  draw(ctx, card, null, hue);
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  cache.set(cacheKey, tex);
  const redraw = (img: HTMLImageElement | null) => {
    draw(ctx, card, img, hue);
    tex.needsUpdate = true;
  };
  if (url) void loadImage(url).then(redraw);
  // fonts may arrive after first paint
  if (document.fonts?.ready) {
    void document.fonts.ready.then(() => (url ? loadImage(url) : Promise.resolve(null))).then(redraw);
  }
  return tex;
}
